import { useState, useEffect, useCallback } from 'react';
import type { FutebolJogoDia, FutebolPartidaAtiva } from '@/shared/types';
import { telasService } from '@/shared/services/telasService';
import { futebolRepository } from '../../data/futebolRepository';

export interface FutebolAdminState {
  jogos:         FutebolJogoDia[];
  partida:       FutebolPartidaAtiva | null;
  /** Tela representativa da empresa — para votação admin mesmo sem jogo ativo. */
  telaId:        string | null;
  loading:       boolean;
  operando:      boolean;
  erro:          string | null;
  limparErro:    () => void;
  ativarJogo:    (fixtureId: number) => Promise<void>;
  desativarJogo: () => Promise<void>;
  recarregar:    () => Promise<void>;
}

export function useFutebolAdmin(empresaId?: string): FutebolAdminState {
  const [jogos,    setJogos]    = useState<FutebolJogoDia[]>([]);
  const [partida,  setPartida]  = useState<FutebolPartidaAtiva | null>(null);
  const [telaId,   setTelaId]   = useState<string | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [operando, setOperando] = useState(false);
  const [erro,     setErro]     = useState<string | null>(null);

  const limparErro = useCallback(() => setErro(null), []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const [jogosData, partidaData, telaData] = await Promise.all([
        futebolRepository.listarJogosDoDia(),
        futebolRepository.buscarPartidaAtivaAdmin(empresaId),
        empresaId ? telasService.buscarPorEmpresa(empresaId) : Promise.resolve(null),
      ]);
      setJogos(jogosData);
      setPartida(partidaData);
      setTelaId(partidaData?.tela_id ?? telaData?.id ?? null);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar os jogos.');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { carregar(); }, [carregar]);

  const ativarJogo = useCallback(async (fixtureId: number) => {
    setOperando(true);
    setErro(null);
    try {
      await futebolRepository.ativarJogo(fixtureId, empresaId);
      await carregar();
    } catch (err) {
      console.error('[FutebolAdmin] ativarJogo error', err);
      setErro(err instanceof Error ? err.message : String(err));
    } finally {
      setOperando(false);
    }
  }, [carregar, empresaId]);

  const desativarJogo = useCallback(async () => {
    setOperando(true);
    setErro(null);
    try {
      await futebolRepository.desativarJogo(empresaId);
      await carregar();
    } catch (err) {
      console.error('[FutebolAdmin] desativarJogo error', err);
      setErro(err instanceof Error ? err.message : String(err));
    } finally {
      setOperando(false);
    }
  }, [carregar, empresaId]);

  return { jogos, partida, telaId, loading, operando, erro, limparErro, ativarJogo, desativarJogo, recarregar: carregar };
}
